import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, LoaderIcon, PackageCheckIcon } from 'lucide-react';
import { builderPrices, lensOptions } from '../data/builder';
import { QuickBuildApi } from '../hooks/useQuickBuild';
import { BuilderVisual } from './builder/BuilderVisual';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { money } from './QuantityStepper';
const EASE = [0.23, 1, 0.32, 1] as const;

/** The live build under the "What's in the box" table: same product visual as the builder above. */
export function QuickBuildPanel({
  build
}: {
  build: QuickBuildApi;
}) {
  const {
    state,
    lines,
    total,
    hint
  } = build;
  return <div id="gear-build" className="scroll-mt-24 border-t border-white/10 bg-ink-900 px-5 py-6 sm:px-7 sm:py-8" data-id="element-1228" data-mp="n85fxql41cyk">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:items-start lg:gap-8" data-id="element-1229" data-mp="rcd67s77v0yw">
        <div className="lg:sticky lg:top-24" data-id="element-1230" data-mp="waeselzxvgel">
          <BuilderVisual state={state} data-id="element-1231" data-mp="158iczj2ujp7u" />
        </div>

        <div data-id="element-1232" data-mp="15a34t0xs0u20">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1233" data-mp="156nra2jr3t2p">Build it right here</p>
          <p className="mt-1 font-display text-2xl font-extrabold uppercase leading-none text-offwhite sm:text-3xl" data-id="element-1234" data-mp="w82948i6yg2g">
            Your X1 Essential, <span className="text-grit-500" data-id="element-1235" data-mp="r9sa1mtcivbk">your way.</span>
          </p>
          <p className="mt-2 text-[14px] leading-relaxed text-steel-400" data-id="element-1236" data-mp="1owhnqtxeuwd9">
            Pick your lens, then add what you want from the list above. Your X1 updates as you go.
          </p>

          <fieldset className="mt-5" data-id="element-1237" data-mp="1f6y3pkxyowgt">
            <legend className="text-[11px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1238" data-mp="1f5wfeerxqdh5">Your lens</legend>
            <div className="mt-2 grid grid-cols-3 gap-2" data-id="element-1239" data-mp="1a71v18ebkgux">
              {lensOptions.map(lens => {
              const active = state.lens === lens.id;
              return <button key={lens.id} type="button" aria-pressed={active} onClick={() => build.setLens(lens.id)} className={`flex items-center justify-center gap-2 border-2 px-2 py-2.5 font-display text-base font-extrabold uppercase tracking-wide transition-[border-color,background-color,color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${active ? 'border-grit-500 bg-grit-500/10 text-offwhite' : 'border-white/15 text-steel-300 hover:border-white/40'}`} data-id="element-1240" data-mp="22vh9nttjgx5q">
                    <span className="h-3 w-3 rounded-full border border-white/40" style={{
                  backgroundColor: lens.swatch
                }} aria-hidden="true" data-id="element-1241" data-mp="34fyqal8bofak" />
                    {lens.name}
                  </button>;
            })}
            </div>
          </fieldset>

          <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" aria-live="polite" data-id="element-1242" data-mp="12mhkzo7nzqiz">
            {lines.map(line => <li key={line.label} className="flex items-baseline justify-between gap-4 py-2.5 text-[14px]" data-id="element-1243" data-mp="1hqkcwnp8khu0">
                <span className="text-steel-200" data-id="element-1244" data-mp="1ctbt6iofw5qn">
                  {line.label}
                  {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                </span>
                <span className="font-semibold tabular-nums text-offwhite" data-id="element-1245" data-mp="1lt79t30rksy2">{money(line.price * line.quantity)}</span>
              </li>)}
          </ul>

          <div className="mt-3 flex items-baseline justify-between" data-id="element-1246" data-mp="20g0hpn7yewg8">
            <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1247" data-mp="1vhp89dlkmnyj">Total · free U.S. shipping</span>
            <span className="font-display text-4xl font-extrabold leading-none text-offwhite tabular-nums" data-id="element-1248" data-mp="29hqel8klunle">{money(total)}</span>
          </div>

          <AnimatePresence mode="wait" initial={false} data-id="element-1249" data-mp="f3yxxp3uz3gg">
            {build.sending ? <motion.div key="sending" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 6
          }} transition={{
            duration: 0.2,
            ease: EASE
          }} role="status" className="mt-4 border-2 border-grit-500 bg-grit-500/10 p-4" data-id="element-1250" data-mp="b36qn1ejr8mt">
                <p className="flex items-center gap-2 font-display text-xl font-extrabold uppercase leading-tight text-offwhite" data-id="element-1251" data-mp="lixugtad9gep">
                  <PackageCheckIcon className="h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1252" data-mp="3pzig1zxx8nym" />
                  You unlocked a better deal.
                </p>
                <p className="mt-1 text-[14px] text-steel-200" data-id="element-1253" data-mp="3ty7rhmzefvfm">
                  Your X1, lenses and case add up to more than the Complete System ({money(builderPrices.complete)}). Taking
                  you there now, with your add-ons.
                </p>
                <div className="mt-3 h-1 overflow-hidden bg-white/10" aria-hidden="true" data-id="element-1254" data-mp="36a81ip9ww923">
                  <motion.div className="h-full bg-grit-500" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 1.4,
                ease: 'linear'
              }} style={{
                transformOrigin: 'left'
              }} data-id="element-1255" data-mp="3b916ftb4urqn" />
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-1256" data-mp="2tqklnifgd8h4">
                  <button type="button" onClick={build.goToComplete} className="inline-flex items-center gap-2 bg-grit-500 px-4 py-2 font-display text-[15px] font-extrabold uppercase tracking-wide text-ink-950 transition-colors duration-150 ease-grit hover:bg-grit-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1257" data-mp="2ppqumg6prdk0">
                    Go now
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1258" data-mp="260q9l32yjnxx" />
                  </button>
                  <button type="button" onClick={build.stay} className="px-2 py-2 text-[13px] text-steel-300 underline underline-offset-2 hover:text-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1259" data-mp="2230vbmlr1x33">
                    Keep my build
                  </button>
                </div>
              </motion.div> : hint.kind !== 'none' ? <motion.div key={hint.kind} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 6
          }} transition={{
            duration: 0.2,
            ease: EASE
          }} className="mt-4 border border-grit-500/50 bg-ink-950 p-4" data-id="element-1260" data-mp="1igh9luio3ihz">
                <p className="font-display text-lg font-extrabold uppercase leading-tight text-offwhite" data-id="element-1261" data-mp="1wdcrmcgwcka6">
                  {hint.kind === 'better' ? `The Complete System is ${money(hint.save)} less` : `Only ${money(hint.diff)} more for the Complete System`}
                </p>
                <p className="mt-1 text-[13px] text-steel-400" data-id="element-1262" data-mp="ifcf08vsoonx">All 3 lenses, hard case, pouch, cloth and 1-year coverage.</p>
                <button type="button" onClick={build.goToComplete} className="mt-3 inline-flex items-center gap-2 border-2 border-grit-500 px-4 py-2 font-display text-[15px] font-extrabold uppercase tracking-wide text-grit-500 transition-[background-color,color] duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1263" data-mp="dfg8o2zmkl57">
                  See the Complete System — {money(builderPrices.complete)}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1264" data-mp="8fboo2zid8a8" />
                </button>
              </motion.div> : null}
          </AnimatePresence>

          <button type="button" onClick={build.checkout} disabled={build.routing || build.sending} className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1265" data-mp="1jva2sl9jmkib">
            {build.routing ? 'Opening checkout' : `Checkout — ${money(total)}`}
            {build.routing ? <LoaderIcon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1266" data-mp="2hsgtfkj6weut" /> : <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1267" data-mp="2mra4z7ex1vsc" />}
          </button>
          {build.notConnected && <p role="alert" className="mt-3 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-1268" data-mp="2vqdoudlwqf7w">
              This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
            </p>}
          <UspsBadge className="mt-3" data-id="element-1269" data-mp="11dnvbwzgb811" />
          <SecureCheckoutNote className="mt-4" data-id="element-1270" data-mp="ttdcg3pkuuwd" />
        </div>
      </div>
    </div>;
}